import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import TailwindSidebar from "./TailwindSidebar";
import HeaderAvatarMenu from "./HeaderAvatarMenu";
import { handleLogout } from "../services/logoutService";
import { useLazyGetLeaderboardQuery, useGetAllProblemsQuery, useSendEmailMutation } from "../store/services/datalyzeProblems";
import EmailModal from "./EmailEnquiry";
import ExlLogo from "../assets/img/exl_Logo01.png";
import { resetJourney, advanceStage, STAGES } from "../tutorials/journeyState";
import usePreloadTour from "../tutorials/usePreloadTour";
import useGameListDomainsTour from "../tutorials/useGameListDomainsTour";
import { useResetTutorialMutation, useLazyGetUserDetailsQuery } from "../store/services/userLogin";
import { REFRESH_USER_RECORDS_KEY } from "../utils/assessmentRetry";
import "./GameList.css";

const GameListLayout = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isExl = true;
    const jwt = useSelector((state) => state?.user?.jwt);
    const userDetails = useSelector((state) => state?.user?.userDetails);
    const coinsCount = useSelector((state) => state?.user?.coinsCount);
    const [getUserDetailsQuery, userDetailsQueryState] = useLazyGetUserDetailsQuery();
    const {
        isFetching: isUserDetailsFetching,
        isLoading: isUserDetailsLoading,
        isUninitialized: isUserDetailsUninitialized,
    } = userDetailsQueryState;
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const startPreloadTour = usePreloadTour();
    const startDomainsTour = useGameListDomainsTour();
    const [resetTutorial] = useResetTutorialMutation();

    const needsRetryUserRefresh = Boolean(location.state?.[REFRESH_USER_RECORDS_KEY]);
    const [showRecordsLoader, setShowRecordsLoader] = useState(needsRetryUserRefresh);

    useEffect(() => {
        if (!needsRetryUserRefresh) {
            return;
        }
        let cancelled = false;
        setShowRecordsLoader(true);
        getUserDetailsQuery()
            .unwrap()
            .catch(() => {})
            .finally(() => {
                if (cancelled) return;
                setShowRecordsLoader(false);
                navigate(
                    { pathname: location.pathname, search: location.search },
                    { replace: true, state: {} }
                );
            });
        return () => {
            cancelled = true;
        };
    }, [
        needsRetryUserRefresh,
        location.pathname,
        location.search,
        getUserDetailsQuery,
        navigate,
    ]);

    useEffect(() => {
        if (!jwt || jwt === "null") return;
        if (location.state?.[REFRESH_USER_RECORDS_KEY]) return;
        getUserDetailsQuery();
    }, [jwt, location.pathname, location.state, getUserDetailsQuery]);

    /** Show sidebar skeleton until fetch-user completes and we have a user profile in Redux. */
    const userHydrated = useMemo(
        () =>
            Boolean(
                userDetails?._id ?? userDetails?.email ?? userDetails?.name,
            ),
        [userDetails],
    );
    const hasJwt = Boolean(jwt && jwt !== "null");
    const showSidebarSkeleton = useMemo(() => {
        if (!hasJwt) return false;
        if (userHydrated) return false;
        return (
            isUserDetailsFetching ||
            isUserDetailsLoading ||
            isUserDetailsUninitialized
        );
    }, [
        hasJwt,
        userHydrated,
        isUserDetailsFetching,
        isUserDetailsLoading,
        isUserDetailsUninitialized,
    ]);

    const handleTakeTutorial = useCallback(async () => {
        try {
            await resetTutorial().unwrap();
        } catch (_) {
            /* API may fail; continue with local reset */
        }
        const isUseCaseOrGameDomainsHome =
            location.pathname === "/gamelist" || location.pathname === "/usecase";
        resetJourney(isUseCaseOrGameDomainsHome ? STAGES.DOMAINS : STAGES.PRELOAD);
        const isOnDomainsPage = isUseCaseOrGameDomainsHome;
        setTimeout(() => {
            if (isOnDomainsPage) {
                startDomainsTour({ onCompleteStage: () => advanceStage() });
            } else {
                startPreloadTour({ onCompleteStage: () => advanceStage() });
            }
        }, 300);
    }, [location.pathname, startPreloadTour, startDomainsTour, resetTutorial]);
    const [openEmail, setOpenEmail] = useState(false);
    const [isLoadingSendingEmail, setIsLoadingSendingEmail] = useState(false);
    const [showEmailSuccess, setShowEmailSuccess] = useState(false);
    const [sendEmail] = useSendEmailMutation();

    const { data: problemsData } = useGetAllProblemsQuery({
        location: "app"
    }, {
        refetchOnMountOrArgChange: true
    });

    /** Leaderboard is fetched only when the user opens it from the sidebar (lazy trigger). */
    const [fetchLeaderboard, { data: leaderboardResponse, isFetching: isLeaderboardFetching }] =
        useLazyGetLeaderboardQuery();

    const handleOpenEmail = () => setOpenEmail(true);
    const handleCloseEmail = () => setOpenEmail(false);

    const handleSendEmail = async (emailData) => {
        setIsLoadingSendingEmail(true);
        try {
            const result = await sendEmail(emailData);
            if (result?.data?.message === "success") {
                setIsLoadingSendingEmail(false);
                handleCloseEmail();
                setShowEmailSuccess(true);
                setTimeout(() => {
                    setShowEmailSuccess(false);
                }, 4000);
            } else {
                setIsLoadingSendingEmail(false);
                handleCloseEmail();
            }
        } catch (err) {
            setIsLoadingSendingEmail(false);
            handleCloseEmail();
        }
    };

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 1279px)");
        const handler = (e) => {
            if (e.matches) setIsSidebarCollapsed(true);
        };
        handler(mq);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    return (
        <div className="h-screen flex flex-col overflow-hidden">
            {/* Background gradient (decorative, keep fixed) */}
            <div
                className="fixed inset-0 rounded-[500px] opacity-10 -z-10"
                style={{
                    background: "var(--gradient-primary)",
                    filter: "blur(100px)",
                }}
            />

            {/* Full-height vertical divider aligning with sidebar (expanded only) */}
            {!isSidebarCollapsed && (
                <div
                    className="pointer-events-none fixed top-0 bottom-0 w-px bg-black/10 z-[60]"
                    style={{ left: 323 }}
                />
            )}

            {/* Header */}
            <header
                className={`shrink-0 flex items-center justify-between bg-white z-50 ${isExl ? 'py-4' : 'py-3'}`}
            >
                {/* Logo zone — matches sidebar width exactly */}
                <div
                    className={`shrink-0 flex items-center justify-start transition-all duration-300 ${isSidebarCollapsed ? 'w-[84px] px-4' : 'w-[323px] px-6'}`}
                >
                    {isExl && (
                        <img
                            src={ExlLogo}
                            alt="ExlLogo"
                            className={`cursor-pointer object-contain transition-all duration-300 ${isSidebarCollapsed ? 'w-12 h-auto' : 'w-[70px] h-6'}`}
                            onClick={() =>
                                navigate(
                                    location.pathname.startsWith("/usecase")
                                        ? "/usecase"
                                        : "/gamelist"
                                )
                            }
                        />
                    )}
                    {!isExl && (
                        <h1
                            className="text-2xl font-extrabold font-['Plus_Jakarta_Sans'] tracking-widest cursor-pointer hover:scale-105 transition-transform duration-300"
                            onClick={() =>
                                navigate(
                                    location.pathname.startsWith("/usecase")
                                        ? "/usecase"
                                        : "/gamelist"
                                )
                            }
                            style={{
                                background: 'linear-gradient(90deg, rgba(251, 78, 11, 1), rgba(0, 255, 133, 1), rgba(0, 80, 113, 1), rgba(251, 78, 11, 1))',
                                backgroundSize: '300% 100%',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                animation: 'gradientShift 4s ease infinite',
                                fontWeight: 900,
                                letterSpacing: '0.05em',
                            }}
                        >
                            DATALYZE
                        </h1>
                    )}
                </div>
                <div className="flex-1 flex items-center justify-end px-5 sm:px-8 md:px-12">
                </div>
                <HeaderAvatarMenu
                    userDetails={userDetails}
                    onLogout={async () => {
                        await handleLogout(dispatch, navigate);
                    }}
                    onProfile={() => {
                        // Placeholder for future profile page navigation
                    }}
                />
            </header>

            {/* Body: sidebar + main content */}
            <div className="flex-1 flex overflow-hidden">
                {showSidebarSkeleton ? (
                    <div
                        className={`shrink-0 h-full flex flex-col z-20 bg-white transition-all duration-300 ${isSidebarCollapsed ? "w-[84px]" : "w-[323px]"}`}
                    >
                        {/* Scrollable top area */}
                        <div className="flex-1 overflow-y-auto p-4 flex flex-col">
                            {/* Collapse toggle placeholder */}
                            <div className="flex justify-end mb-2">
                                <div className="w-8 h-8 rounded bg-exl-light-blue animate-pulse" />
                            </div>

                            {/* Nav items: Home, Leaderboard, Profile name */}
                            <div className="space-y-1 mb-4">
                                {[{ w: "w-12" }, { w: "w-24" }, { w: "w-20" }].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl">
                                        <div className="w-5 h-5 rounded bg-exl-light-blue animate-pulse shrink-0" />
                                        <div className={`h-4 ${item.w} bg-exl-light-blue rounded animate-pulse`} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sticky footer */}
                        <div className="p-4 pt-0 mt-auto shrink-0">
                            {/* Overall Progress card */}
                            <div className="rounded-lg w-full flex flex-col p-3 gap-2 mb-3 bg-exl-light-blue">
                                <div className="h-3 w-28 bg-white/60 rounded animate-pulse" />
                                <div className="h-4 w-24 bg-white/60 rounded animate-pulse" />
                                <div className="w-full h-[10px] bg-white/40 rounded-[5px] overflow-hidden">
                                    <div className="h-full bg-white/60 rounded-[5px] w-1/4 animate-pulse" />
                                </div>
                            </div>

                            {/* Use cases completed row */}
                            <div className="flex items-center bg-exl-light-blue/60 px-3 py-2.5 rounded-xl w-full mb-3">
                                <div className="h-3 w-40 bg-exl-light-blue rounded animate-pulse mr-2" />
                                <div className="h-3 w-10 bg-gray-300 rounded animate-pulse" />
                            </div>

                            {/* Your Rank row */}
                            <div className="flex items-center bg-exl-light-blue/60 px-3 py-2.5 rounded-xl w-full mb-4">
                                <div className="h-3 w-20 bg-exl-light-blue rounded animate-pulse mr-2" />
                                <div className="h-3 w-8 bg-gray-300 rounded animate-pulse" />
                            </div>

                            {/* XP / Coins card */}
                            <div className="h-[75px] rounded-2xl bg-exl-light-blue flex flex-row items-center px-4 gap-3 w-full mb-4">
                                <div className="w-9 h-9 rounded-full bg-white/40 animate-pulse shrink-0" />
                                <div className="flex flex-col gap-1.5">
                                    <div className="h-6 w-12 bg-white/60 rounded animate-pulse" />
                                    <div className="h-3 w-28 bg-white/40 rounded animate-pulse" />
                                </div>
                            </div>

                            {/* Retake Tutorial button */}
                            <div className="w-full h-9 bg-exl-light-blue rounded-[12px] animate-pulse mb-2" />

                            {/* Logout button */}
                            <div className="w-full h-9 bg-red-50 rounded-[12px] animate-pulse mb-3" />

                            {/* Divider + version */}
                            <div className="w-full h-[1px] bg-exl-light-blue my-2" />
                            <div className="h-3 w-20 bg-exl-light-blue rounded animate-pulse mx-auto mb-1" />
                            <div className="h-3 w-32 bg-exl-light-blue rounded animate-pulse mx-auto" />
                        </div>
                    </div>
                ) : (
                    <TailwindSidebar
                        userDetails={userDetails}
                        leaderboardData={leaderboardResponse?.data}
                        userRank={leaderboardResponse?.currentUserRank}
                        fetchLeaderboard={fetchLeaderboard}
                        isLeaderboardFetching={isLeaderboardFetching}
                        data={problemsData?.data || []}
                        coinsCount={coinsCount}
                        isExl={isExl}
                        onContactUsClick={handleOpenEmail}
                        onCollapseChange={setIsSidebarCollapsed}
                        isCollapsedProp={isSidebarCollapsed}
                        onTakeTutorial={handleTakeTutorial}
                        hasCompletedTutorial={userDetails?.hasCompletedTutorial === true}
                        onLogout={async () => {
                            await handleLogout(dispatch, navigate);
                        }}
                    />
                )}

                {/* Main Content */}
                <main className="flex-1 flex flex-col overflow-hidden bg-white transition-all duration-300">
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-10">
                        {children}
                    </div>
                </main>
            </div>

            {/* Email Modal */}
            <EmailModal
                isLoading={isLoadingSendingEmail}
                open={openEmail}
                handleClose={handleCloseEmail}
                handleSendEmail={handleSendEmail}
            />

            {/* Email Success Message */}
            {showEmailSuccess && (
                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 50, opacity: 0 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="fixed top-1/2 left-[40%] -translate-x-[30%] -translate-y-1/2 flex items-center px-3 h-10 w-[300px] rounded-lg shadow-lg z-[1000]"
                    style={{ backgroundColor: "var(--success-color)" }}
                >
                    <p className="text-[var(--text-color)] font-['Plus_Jakarta_Sans'] text-base font-medium leading-6 tracking-[-0.5px]">
                        Email sent Successfully
                    </p>
                </motion.div>
            )}

            {showRecordsLoader &&
                typeof document !== "undefined" &&
                createPortal(
                    <motion.div
                        className="usecase-records-loader"
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <div className="usecase-records-loader__spinner" aria-hidden />
                        <p className="usecase-records-loader__text">
                            Getting your latest records…
                        </p>
                    </motion.div>,
                    document.body
                )}
        </div>
    );
};

export default GameListLayout;

